/**
 * Projects the currently visible Flow aggregation into one hierarchical breakdown per direction.
 * Its rows mirror chart series, including ranking, Remaining members and legend visibility.
 * Required to create the bucket-to-source tree and reflect which Flow series are hidden on the chart.
 */
import {
    aggregateFlowRange,
    FlowAggregation,
} from './flow-dashboard-aggregation';
import {
    FlowBreakdownRow,
    FlowBreakdownSort,
    FlowRangeAggregation,
    FLOW_IN_PREFIX,
    FLOW_OUT_PREFIX,
    FLOW_REMAINING_COLOR,
    FLOW_REMAINING_LABEL,
} from './flow-dashboard.models';

import { FlowPresentation } from './flow-dashboard-presentation';
type FlowDirection = 'in' | 'out';

export interface FlowBreakdownOptions {
    aggregation: FlowAggregation;
    presentation: FlowPresentation;
    visibleDates: Set<string>;
    hiddenSeries: Set<string>;
    threshold: number;
    rangeAggregation: FlowRangeAggregation;
    /** Top-level row order; children remain value-ranked. */
    sort: FlowBreakdownSort;
}

/** Build one side's breakdown tree from the chart's current ranking and visibility state. */
export function buildFlowBreakdown(
    dir: FlowDirection,
    options: FlowBreakdownOptions
): FlowBreakdownRow[] {
    const { aggregation, presentation, visibleDates } = options;
    const big = dir === 'in' ? presentation.inflowBig : presentation.outflowBig;
    const small =
        dir === 'in' ? presentation.inflowSmall : presentation.outflowSmall;
    const colors = dir === 'in' ? presentation.colorIn : presentation.colorOut;
    const prefix = dir === 'in' ? FLOW_IN_PREFIX : FLOW_OUT_PREFIX;
    const byDateKey = dir === 'in' ? 'inflowByDate' : 'outflowByDate';
    const valOf = (group: string) =>
        aggregateFlowRange(
            aggregation.groups[group][byDateKey],
            visibleDates,
            options.rangeAggregation
        );
    // Detail groups can overlap for non-additive metrics, so their shares are deliberately approximate.
    const grand =
        [...big, ...small].reduce((total, key) => total + valOf(key), 0) || 1;
    /** Ranking matches chart stacking; other modes use the visible-range value. */
    const order = (rows: FlowBreakdownRow[]): FlowBreakdownRow[] => {
        if (options.sort === 'ranking') {
            return rows;
        }
        const direction = options.sort === 'asc' ? 1 : -1;
        return [...rows].sort(
            (left, right) => direction * (left.value - right.value)
        );
    };
    const sourceRow = (
        name: string,
        seriesName: string,
        value: number,
        color: string
    ): FlowBreakdownRow => ({
        name,
        value,
        share: value / grand,
        color,
        seriesName,
        hidden: !!seriesName && options.hiddenSeries.has(seriesName),
    });
    const remaining = (
        keys: string[],
        valueOf: (key: string) => number,
        seriesName: string,
        keySuffix: string
    ): FlowBreakdownRow => {
        const hidden = !!seriesName && options.hiddenSeries.has(seriesName);
        const children = keys
            .map((key) => ({
                name: key,
                value: valueOf(key),
                share: valueOf(key) / grand,
                color: FLOW_REMAINING_COLOR,
                seriesName: '',
                hidden,
            }))
            .sort((left, right) => right.value - left.value);
        const value = keys.reduce((total, key) => total + valueOf(key), 0);
        return {
            name: `${FLOW_REMAINING_LABEL} (<${(
                options.threshold * 100
            ).toFixed(1)}%, ${keys.length})`,
            value,
            share: value / grand,
            color: FLOW_REMAINING_COLOR,
            seriesName,
            hidden,
            key: `${prefix} ${keySuffix}${FLOW_REMAINING_LABEL}`,
            children,
        };
    };

    if (!aggregation.groupByBucket) {
        const rows = big.map((key) =>
            sourceRow(key, `${prefix} ${key}`, valOf(key), colors[key])
        );
        if (small.length) {
            rows.push(
                remaining(small, valOf, `${prefix} ${FLOW_REMAINING_LABEL}`, '')
            );
        }
        return order(rows);
    }

    const buckets =
        dir === 'in' ? presentation.bucketInBig : presentation.bucketOutBig;
    const smallBuckets =
        dir === 'in' ? presentation.bucketInSmall : presentation.bucketOutSmall;
    const hasSplit = !(big.length === 1 && big[0] === 'Total' && !small.length);
    const sourceValue = (bucket: string, group: string) =>
        aggregateFlowRange(
            aggregation.byBucket[bucket]?.[group]?.[byDateKey] ?? {},
            visibleDates,
            options.rangeAggregation
        );
    const bucketValue = (bucket: string) =>
        Object.keys(aggregation.byBucket[bucket] ?? {}).reduce(
            (total, group) => total + sourceValue(bucket, group),
            0
        );
    const bucketRow = (bucket: string): FlowBreakdownRow => {
        const seriesName = `${prefix} ${bucket}`;
        const color =
            (dir === 'in'
                ? presentation.bucketColorIn
                : presentation.bucketColorOut)[bucket] ?? FLOW_REMAINING_COLOR;
        const children: FlowBreakdownRow[] = [];
        if (hasSplit) {
            const bigInBucket = big.filter(
                (group) => sourceValue(bucket, group) > 0
            );
            const smallInBucket = small.filter(
                (group) => sourceValue(bucket, group) > 0
            );
            children.push(
                ...bigInBucket
                    .map((group) =>
                        sourceRow(group, '', sourceValue(bucket, group), color)
                    )
                    .sort((left, right) => right.value - left.value)
            );
            if (smallInBucket.length) {
                children.push(
                    remaining(
                        smallInBucket,
                        (group) => sourceValue(bucket, group),
                        '',
                        `${bucket} / `
                    )
                );
            }
        }
        const value = bucketValue(bucket);
        return {
            name: bucket,
            value,
            share: value / grand,
            color,
            seriesName,
            hidden: options.hiddenSeries.has(seriesName),
            key: seriesName,
            children,
        };
    };

    const rows = buckets.map(bucketRow);
    if (smallBuckets.length) {
        const seriesName = `${prefix} ${FLOW_REMAINING_LABEL}`;
        const members = smallBuckets
            .map((bucket) =>
                sourceRow(bucket, '', bucketValue(bucket), FLOW_REMAINING_COLOR)
            )
            .sort((left, right) => right.value - left.value);
        const value = smallBuckets.reduce(
            (total, bucket) => total + bucketValue(bucket),
            0
        );
        rows.push({
            name: `${FLOW_REMAINING_LABEL} (<${(
                options.threshold * 100
            ).toFixed(1)}%, ${smallBuckets.length})`,
            value,
            share: value / grand,
            color: FLOW_REMAINING_COLOR,
            seriesName,
            hidden: options.hiddenSeries.has(seriesName),
            key: `${prefix} bucket-${FLOW_REMAINING_LABEL}`,
            children: members,
        });
    }
    return order(rows);
}
